/**
 * Information architecture for the app shell.
 *
 * Nine primary sections live in the sidebar; each section's pages are
 * reached through a shared tab bar (<SectionTabs>). Every tab points at an
 * existing route, so deep links and bookmarks keep working — this file only
 * decides which section and tab "own" a URL.
 *
 * Ownership rules, in order:
 *   1. A tab owns its own path and everything below it (`/bills` owns
 *      `/bills/119-hr-1`), plus any extra prefixes in `owns`.
 *   2. Board routes under bills/issues (`/bills/:ref/whip-count`) belong to
 *      Whip boards, not to Legislation/Issues, so they are matched first.
 *   3. `/proposals` belongs to Whip boards ("Sharing & requests"), except
 *      when opened from the inbox (`?from=inbox`), where it is the inbox's
 *      "Proposals & requests" view.
 */
import type { LucideIcon } from "lucide-react";
import {
  Home,
  Landmark,
  Map as MapIcon,
  ScrollText,
  Users,
  Kanban,
  Target,
  Handshake,
  Sparkles,
} from "lucide-react";

export interface NavTab {
  id: string;
  label: string;
  /** Route the tab links to (may carry a query string). */
  to: string;
  /** Extra path prefixes this tab owns besides `to`'s own path. */
  owns?: string[];
  /** Only an exact path match selects this tab (no sub-paths). */
  exact?: boolean;
  /** Second-level tabs shown under this tab (e.g. the unified inbox). */
  children?: NavTab[];
}

export interface NavSection {
  id: string;
  label: string;
  Icon: LucideIcon;
  tabs: NavTab[];
}

export const INBOX_FROM_PARAM = "from";
export const INBOX_FROM_VALUE = "inbox";

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "home",
    label: "Home",
    Icon: Home,
    tabs: [
      { id: "dashboard", label: "Dashboard", to: "/dashboard", owns: ["/coalition-feed"] },
      {
        id: "inbox",
        label: "Inbox",
        to: "/notifications",
        children: [
          { id: "notifications", label: "Notifications", to: "/notifications" },
          { id: "discussions", label: "Discussions", to: "/discussions" },
          { id: "inbox-proposals", label: "Proposals & requests", to: `/proposals?${INBOX_FROM_PARAM}=${INBOX_FROM_VALUE}` },
        ],
      },
    ],
  },
  {
    id: "congress",
    label: "Congress",
    Icon: Landmark,
    tabs: [
      { id: "congress-overview", label: "Overview", to: "/congress" },
      { id: "congress-members", label: "Members", to: "/congress/members" },
      { id: "congress-bills", label: "Bills", to: "/congress/bills" },
      { id: "congress-votes", label: "Votes", to: "/congress/votes" },
      { id: "congress-committees", label: "Committees & hearings", to: "/congress/committees" },
      { id: "congress-nominations", label: "Nominations", to: "/congress/nominations" },
    ],
  },
  {
    id: "states",
    label: "States",
    Icon: MapIcon,
    // State pages (/states/:code) belong to "All states".
    tabs: [{ id: "states", label: "All states", to: "/states" }],
  },
  {
    id: "legislation",
    label: "Legislation",
    Icon: ScrollText,
    tabs: [
      { id: "bills", label: "Tracked bills", to: "/bills", owns: ["/legislation"] },
      { id: "discover", label: "Discover", to: "/discover" },
      { id: "monitors", label: "Monitors", to: "/monitors" },
      { id: "provisions", label: "Provisions", to: "/provisions" },
    ],
  },
  {
    id: "people",
    label: "People",
    Icon: Users,
    tabs: [
      { id: "members", label: "Members", to: "/members", owns: ["/legislator-activity"] },
      { id: "representatives", label: "My representatives", to: "/representatives" },
      { id: "relationships", label: "Relationships", to: "/relationships" },
      { id: "designations", label: "Champions & targets", to: "/designations" },
      { id: "caucuses", label: "Caucuses", to: "/caucuses" },
      { id: "contacts", label: "Contacts", to: "/contacts" },
      { id: "accounts", label: "Organizations", to: "/accounts", owns: ["/companies"] },
      { id: "treasury", label: "Treasury & pipelines", to: "/treasury", owns: ["/pipelines"] },
    ],
  },
  {
    id: "whip",
    label: "Whip boards",
    Icon: Kanban,
    tabs: [
      { id: "whip-boards", label: "All boards", to: "/whip-boards" },
      { id: "map", label: "Map", to: "/map" },
      { id: "scorecards", label: "Scorecards", to: "/scorecards" },
      { id: "proposals", label: "Sharing & requests", to: "/proposals" },
    ],
  },
  {
    id: "issues",
    label: "Issues",
    Icon: Target,
    tabs: [
      { id: "issues", label: "Issues", to: "/issues" },
      { id: "research", label: "Research", to: "/research" },
      { id: "intake", label: "Intelligence", to: "/intake", owns: ["/intelligence"] },
    ],
  },
  {
    id: "coalitions",
    label: "Coalitions",
    Icon: Handshake,
    tabs: [{ id: "coalitions", label: "Coalitions", to: "/coalitions" }],
  },
  {
    id: "ai",
    label: "AI Studio",
    Icon: Sparkles,
    tabs: [
      { id: "reports", label: "Reports", to: "/reports", owns: ["/pages", "/ai-canvas"] },
      { id: "presentations", label: "Presentations", to: "/presentations" },
      { id: "live", label: "Live ops", to: "/live" },
      { id: "tool-history", label: "Tool history", to: "/tool-history" },
    ],
  },
];

/** Account, preferences and resources: the compact footer menu. */
export interface FooterNavItem {
  id: string;
  label: string;
  to: string;
  /** Rendered as a full-page link outside the React app. */
  external?: boolean;
}

export const FOOTER_NAV: FooterNavItem[] = [
  { id: "personal-setup", label: "My state & interests", to: "/personal-setup" },
  { id: "settings", label: "Settings", to: "/settings" },
  { id: "feedback", label: "Feedback", to: "/feedback" },
  { id: "changelog", label: "Changelog", to: "/changelog" },
  { id: "connect", label: "Connect your AI", to: "/connect", external: true },
  { id: "mcp-tools", label: "Available MCP tools", to: "/tool-history?section=capabilities" },
  { id: "docs", label: "Docs", to: "/docs", external: true },
];

export function tabPath(tab: Pick<NavTab, "to">): string {
  return tab.to.split("?")[0];
}

function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

function tabOwns(tab: NavTab, pathname: string): boolean {
  const own = tabPath(tab);
  if (tab.exact) return pathname === own;
  return under(pathname, own) || (tab.owns ?? []).some((prefix) => under(pathname, prefix));
}

export interface ResolvedNav {
  section: NavSection;
  tab: NavTab;
  /** Selected second-level tab, when the tab has children. */
  child: NavTab | null;
  /** True when the URL is the tab's own page (not a detail page below it). */
  isTabPage: boolean;
}

function find(sectionId: string, tabId: string): { section: NavSection; tab: NavTab } {
  const section = NAV_SECTIONS.find((s) => s.id === sectionId)!;
  return { section, tab: section.tabs.find((t) => t.id === tabId)! };
}

/** Which section/tab owns this URL, or null for pages outside the sections. */
export function resolveNav(pathname: string, search = ""): ResolvedNav | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  const params = new URLSearchParams(search);

  // Board routes nested under bills and issues are whip boards.
  if (/^\/(bills|issues)\/[^/]+\/whip-count$/.test(path)) {
    return { ...find("whip", "whip-boards"), child: null, isTabPage: false };
  }
  if (path === "/proposals" && params.get(INBOX_FROM_PARAM) === INBOX_FROM_VALUE) {
    const { section, tab } = find("home", "inbox");
    return { section, tab, child: tab.children!.find((c) => c.id === "inbox-proposals")!, isTabPage: true };
  }

  for (const section of NAV_SECTIONS) {
    // Most specific tab first, so a nested tab path beats its parent's.
    const ordered = [...section.tabs].sort((a, b) => tabPath(b).length - tabPath(a).length);
    for (const tab of ordered) {
      if (tab.children) {
        const child = tab.children.find((c) => tabOwns(c, path) && tabPath(c) !== "/proposals");
        if (child) return { section, tab, child, isTabPage: path === tabPath(child) };
      }
      if (tabOwns(tab, path)) {
        const child = tab.children?.[0] ?? null;
        return { section, tab, child, isTabPage: path === tabPath(tab) };
      }
    }
  }
  return null;
}

/** The footer item that owns this URL, if any (for active styling). */
export function resolveFooterNav(pathname: string, search = ""): FooterNavItem | null {
  const params = new URLSearchParams(search);
  if (pathname === "/tool-history" && params.get("section") === "capabilities") {
    return FOOTER_NAV.find((item) => item.id === "mcp-tools") ?? null;
  }
  return FOOTER_NAV.find((item) => !item.external && !item.to.includes("?") && under(pathname, item.to)) ?? null;
}

/** Carry the current workspace on in-app links (`?workspace_id=`). */
export function workspacePath(path: string, workspaceId?: string | null): string {
  const url = new URL(path, "https://app.invalid");
  if (workspaceId) url.searchParams.set("workspace_id", workspaceId);
  return url.pathname + url.search;
}
